/**
 * Global Error Tracking Service
 * Captures all JavaScript errors, unhandled promise rejections, and network errors
 * Sends to Datadog for monitoring and debugging
 */

import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { sanitizeProperties } from './hipaa-compliance';

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string | number;
  extra?: Record<string, unknown>;
}

interface NetworkErrorDetails {
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  duration?: number;
  requestBody?: unknown;
}

/**
 * Check if Datadog is initialized
 */
const isDatadogReady = (): boolean => {
  try {
    return datadogRum.getInternalContext() !== undefined;
  } catch {
    return false;
  }
};

/**
 * Initialize global error handlers
 * Call this once when the app starts
 */
export function initializeErrorTracking(): void {
  if (typeof window === 'undefined') return;

  // Track unhandled JavaScript errors
  window.addEventListener('error', (event: ErrorEvent) => {
    captureError(event.error || new Error(event.message), {
      component: 'window',
      action: 'unhandled_error',
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));

    captureError(error, {
      component: 'window',
      action: 'unhandled_promise_rejection',
    });
  });

  // Log initialization
  if (import.meta.env.DEV) {
    console.info('[ErrorTracking] Global error handlers initialized');
  }
}

/**
 * Capture and track an error to Datadog
 */
export function captureError(
  error: Error | unknown,
  context?: ErrorContext
): void {
  if (!isDatadogReady()) {
    console.error('[ErrorTracking] Datadog not initialized, error not tracked:', error);
    return;
  }

  const errorObj = error instanceof Error ? error : new Error(String(error));

  const properties = sanitizeProperties({
    // Error details
    error_type: errorObj.name,
    error_message: errorObj.message,

    // Context
    component: context?.component || 'unknown',
    action: context?.action || 'error',
    user_id: context?.userId,

    // Environment
    url: window.location.href,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),

    // Additional context
    ...context?.extra,
  });

  // Use Datadog RUM addError method
  datadogRum.addError(errorObj, properties);
}

/**
 * Track a network/API error
 */
export function captureNetworkError(
  details: NetworkErrorDetails,
  error?: Error,
  context?: ErrorContext
): void {
  if (!isDatadogReady()) {
    console.error('[ErrorTracking] Datadog not initialized, network error not tracked');
    return;
  }

  const properties = sanitizeProperties({
    // Network error details
    request_url: details.url,
    request_method: details.method,
    response_status: details.status,
    response_status_text: details.statusText,
    request_duration_ms: details.duration,

    // Error details if available
    error_type: error?.name,
    error_message: error?.message || `HTTP ${details.status}: ${details.statusText}`,

    // Categorize the error
    error_category: categorizeHttpError(details.status),

    // Context
    component: context?.component || 'api',
    action: context?.action || 'network_error',
    user_id: context?.userId,

    // Environment
    page_url: window.location.href,
    timestamp: new Date().toISOString(),

    ...context?.extra,
  });

  // Track as both an action and error in Datadog
  datadogRum.addAction('network_error', properties);

  if (details.status && details.status >= 500) {
    // Server errors get tracked as errors
    const networkError = error || new Error(`Network Error: ${details.method} ${details.url} - ${details.status}`);
    datadogRum.addError(networkError, properties);
  }
}

/**
 * Track a custom error event (for try-catch blocks)
 */
export function trackError(
  eventName: string,
  error: Error | string,
  context?: ErrorContext
): void {
  if (!isDatadogReady()) return;

  const errorObj = error instanceof Error ? error : new Error(error);

  const properties = sanitizeProperties({
    error_type: errorObj.name,
    error_message: errorObj.message,
    component: context?.component,
    action: context?.action,
    user_id: context?.userId,
    url: window.location.href,
    timestamp: new Date().toISOString(),
    ...context?.extra,
  });

  datadogRum.addAction(eventName, properties);
  datadogRum.addError(errorObj, properties);
}

/**
 * Create an error-tracking wrapper for async functions
 */
export function withErrorTracking<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  context: ErrorContext
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureError(error, context);
      throw error;
    }
  }) as T;
}

// Helper functions

function categorizeHttpError(status?: number): string {
  if (!status) return 'network_failure';
  if (status >= 500) return 'server_error';
  if (status === 404) return 'not_found';
  if (status === 403) return 'forbidden';
  if (status === 401) return 'unauthorized';
  if (status === 400) return 'bad_request';
  if (status === 429) return 'rate_limited';
  if (status >= 400) return 'client_error';
  return 'unknown';
}

// Console log wrapper for production (strips logs)
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
  debug: console.debug,
};

/**
 * Strip console logs in production
 * Call this in production to prevent console output
 */
export function stripConsoleLogs(): void {
  if (import.meta.env.PROD && !import.meta.env.VITE_ENABLE_CONSOLE_LOGS) {
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
    // Keep warn and error for important messages
    console.warn = (...args: unknown[]) => {
      // Track warnings to Datadog in production
      if (isDatadogReady()) {
        datadogLogs.logger.warn(args.map(String).join(' '), {
          timestamp: new Date().toISOString(),
        });
      }
    };
    console.error = (...args: unknown[]) => {
      // Track errors to Datadog in production
      if (isDatadogReady()) {
        datadogLogs.logger.error(args.map(String).join(' '), {
          timestamp: new Date().toISOString(),
        });
      }
      // Also call original error for debugging
      originalConsole.error.apply(console, args);
    };
  }
}

/**
 * Restore original console methods (for debugging in production)
 */
export function restoreConsoleLogs(): void {
  Object.assign(console, originalConsole);
}
